// The demo venue's live screens, answered from the simulation (spec/24 §2.2, §4.2).
//
// The demo console answers every path past sign-in in the browser (`backend.ts`). The ones here are the screens the
// simulation models — the Floor and its tiles, a station's session and its timeline, starting, ending and adding
// time, messages to a PC, calls for help and the kitchen — and they read and write the shared demo venue
// (`@clavian/demo`), so what the kiosk tab does shows here and what staff do here reaches the kiosk. Every other path
// falls through to the console's own fixtures (`src/testing/fake-server.ts`), which share this venue's organisation,
// venue and player ids.
//
// Each answer is the contract's shape, typed with the generated client's types, as the api would send it. Money is
// integer paise with `INR`; times are RFC 3339 UTC from the clock the backend is handed.

import type {
  FloorStationState,
  KitchenTicket,
  Session,
  SessionEvent,
  Station,
  StationCall,
  StationClass,
  Zone,
} from '@clavian/api-client';
import {
  addTime,
  cancelCallAt,
  classById,
  DEMO_CLASSES,
  DEMO_CURRENCY,
  DEMO_GRACE_SECONDS,
  DEMO_ORGANIZATION,
  DEMO_STATIONS,
  DEMO_VENUE,
  DEMO_ZONES,
  endSession,
  playerById,
  sendMessage,
  sessionById,
  sessionPhase,
  setCallState,
  setOrderStatus,
  startSession,
  stationById,
  stationViews,
  type DemoCall,
  type DemoOrder,
  type DemoOrderStatus,
  type DemoSession,
  type DemoState,
  type DemoStore,
} from '@clavian/demo';

/** The nil player id a walk-in's session carries on the wire (ADR-0034, `WALK_IN_PLAYER_ID`). */
const WALK_IN = 'pla_00000000000000000000000000';

/** A response the backend turns into a `Response`. */
export interface DemoAnswer {
  readonly status: number;
  readonly body: unknown;
}

const iso = (ms: number): string => new Date(ms).toISOString();
const money = (amount: number) => ({ amount, currency: DEMO_CURRENCY });
/** When the demo venue's configuration was "made": a fixed instant, so nothing moves on reload. */
const CREATED = '2026-08-01T06:30:00.000Z';

const ORGANIZATION_PATH = `/v1/organizations/${DEMO_ORGANIZATION.id}`;
const VENUE_PATH = `${ORGANIZATION_PATH}/venues/${DEMO_VENUE.id}`;

/** The paths this module answers. */
export const DEMO_VENUE_PATHS = {
  floor: new RegExp(`^${VENUE_PATH}/floor$`),
  floorLive: new RegExp(`^${VENUE_PATH}/floor/live$`),
  stations: new RegExp(`^${VENUE_PATH}/stations$`),
  stationClasses: new RegExp(`^${VENUE_PATH}/station-classes$`),
  zones: new RegExp(`^${VENUE_PATH}/zones$`),
  stationSession: new RegExp(`^${VENUE_PATH}/stations/(stn_[0-9a-z]+)/session$`),
  stationHealth: new RegExp(`^${VENUE_PATH}/stations/(stn_[0-9a-z]+)/health$`),
  stationTelemetry: new RegExp(`^${VENUE_PATH}/stations/(stn_[0-9a-z]+)/telemetry$`),
  needsAttention: new RegExp(`^${VENUE_PATH}/needs-attention$`),
  startSession: new RegExp(`^${VENUE_PATH}/sessions$`),
  session: new RegExp(`^${ORGANIZATION_PATH}/sessions/(ses_[0-9a-z]+)$`),
  sessionTimeline: new RegExp(`^${ORGANIZATION_PATH}/sessions/(ses_[0-9a-z]+)/timeline$`),
  sessionAction: new RegExp(`^${ORGANIZATION_PATH}/sessions/(ses_[0-9a-z]+)/(comp|sell-time|message|end)$`),
  calls: new RegExp(`^${VENUE_PATH}/calls$`),
  callAction: new RegExp(`^${VENUE_PATH}/calls/(cll_[0-9a-z]+)/(acknowledge|resolve)$`),
  kitchenTickets: new RegExp(`^${VENUE_PATH}/kitchen/tickets$`),
  kitchenTicketState: new RegExp(`^${VENUE_PATH}/kitchen/tickets/(ktk_[0-9a-z]+)/state$`),
} as const;

/** Every answer from the simulated venue has this request id: nothing reached a server, so there is no trace to quote. */
export const DEMO_REQUEST_ID = 'req_00000000000000000000000000';

const problem = (status: number, code: string, title: string, detail?: string): DemoAnswer => ({
  status,
  body: {
    type: `https://errors.clavian.io/${code.replaceAll('_', '-')}`,
    title,
    status,
    code,
    requestId: DEMO_REQUEST_ID,
    ...(detail === undefined ? {} : { detail }),
  },
});

// -----------------------------------------------------------------------------------------------
// The configuration: classes, zones, stations
// -----------------------------------------------------------------------------------------------

const scope = { organizationId: DEMO_ORGANIZATION.id, venueId: DEMO_VENUE.id, createdAt: CREATED, updatedAt: CREATED } as const;

export function demoStationClasses(): StationClass[] {
  return DEMO_CLASSES.map((entry) => ({
    ...scope,
    id: entry.id,
    name: entry.name,
    colorToken: entry.colorToken,
    description: entry.description,
    ageLimit: null,
    pricingMode: 'per_player',
    defaultCapacity: 1,
  })) as StationClass[];
}

export function demoZones(): Zone[] {
  return DEMO_ZONES.map((zone) => ({ ...scope, id: zone.id, name: zone.name, colorToken: zone.colorToken, floor: 0 })) as Zone[];
}

export function demoFloor(state: DemoState, now: number): FloorStationState[] {
  return stationViews(state, now).map((view) => ({
    stationId: view.station.id,
    number: view.station.number,
    name: view.station.name,
    kind: 'pc',
    classId: view.station.classId,
    zoneId: view.station.zoneId,
    capacity: 1,
    status: 'active',
    disabledReason: null,
    managed: true,
    // Every demo PC holds its socket: the simulation is the station (spec/24 §2.2).
    online: true,
    mode: view.mode,
    lastSeenAt: iso(now),
    health: { openAlerts: 0, severity: null, kinds: [] },
    title: null,
  })) as FloorStationState[];
}

export function demoStations(state: DemoState, now: number): Station[] {
  return demoFloor(state, now).map((live) => ({
    ...scope,
    id: live.stationId,
    number: live.number,
    name: live.name,
    kind: 'pc',
    role: 'station',
    managed: true,
    classId: live.classId,
    zoneId: live.zoneId,
    capacity: 1,
    status: 'active',
    disabledReason: null,
    mode: live.mode ?? 'locked',
    deviceId: `dev_${live.stationId.slice('stn_'.length)}`,
  })) as Station[];
}

// -----------------------------------------------------------------------------------------------
// Sessions
// -----------------------------------------------------------------------------------------------

const SOURCE: Readonly<Record<DemoSession['funding']['kind'], Session['currentSource']>> = {
  pass: 'pass',
  wallet: 'wallet',
  comp: 'comp',
  paid: 'postpaid',
};

/** What the session has been charged so far: its wallet and counter time at the class rate, by the minute. */
function chargedAmount(session: DemoSession, elapsedSeconds: number): number {
  const stationClass = classById(stationById(session.stationId)?.classId ?? '');
  const rate = stationClass?.hourlyRate ?? 0;
  const paid = session.funding.kind === 'wallet' || session.funding.kind === 'paid' ? Math.min(elapsedSeconds, session.fundedSeconds) : 0;
  const soldLater = session.added.filter((entry) => entry.kind === 'paid').reduce((sum, entry) => sum + entry.seconds, 0);
  return Math.round(((paid + soldLater) / 3_600) * rate);
}

export function demoSessionDto(session: DemoSession, now: number): Session {
  const phase = sessionPhase(session, now);
  const elapsed = Math.max(0, Math.floor(((phase.endedAt ?? now) - session.startedAt) / 1000));
  const status: Session['status'] =
    phase.status === 'running' ? 'active' : phase.status === 'locked' ? 'locked' : phase.status === 'grace' ? 'grace' : 'ended';
  const endReason: Session['endReason'] =
    phase.endReason === null ? null : phase.endReason === 'logout' ? 'logout' : phase.endReason === 'staff' ? 'staff_ended' : 'grace_expired';
  const label = session.funding.kind === 'wallet' ? null : session.funding.label;
  const plan: Session['plan'] =
    status === 'ended'
      ? []
      : status === 'grace'
        ? [{ source: 'grace', label: null, seconds: Math.max(0, Math.ceil((phase.graceEndsAt - now) / 1000)) }]
        : [{ source: SOURCE[session.funding.kind] ?? 'postpaid', label, seconds: phase.secondsLeft }];
  return {
    id: session.id,
    organizationId: DEMO_ORGANIZATION.id,
    venueId: DEMO_VENUE.id,
    stationId: session.stationId,
    playerId: session.playerId ?? WALK_IN,
    status,
    startedAt: iso(session.startedAt),
    endedAt: phase.endedAt === null ? null : iso(phase.endedAt),
    endReason,
    currentSource: status === 'ended' ? null : status === 'grace' ? 'grace' : (SOURCE[session.funding.kind] ?? null),
    timeLeftSeconds: phase.secondsLeft,
    fundedEndAt: status === 'ended' ? null : iso(phase.endsAt),
    plan,
    charged: { chargedSeconds: session.funding.kind === 'pass' || session.funding.kind === 'comp' ? Math.min(elapsed, phase.totalSeconds) : 0, chargedAmount: money(chargedAmount(session, elapsed)) },
    serverTime: iso(now),
    graceSeconds: DEMO_GRACE_SECONDS,
    ...(status === 'grace' ? { graceEndsAt: iso(phase.graceEndsAt) } : {}),
    ...(session.playerId === null ? { walkIn: true } : {}),
  } as Session;
}

/** The session's history, as the timeline lists it: started, time added, locked, warned, grace and the end. */
function demoTimeline(session: DemoSession, now: number): SessionEvent[] {
  const phase = sessionPhase(session, now);
  let serial = 0;
  const event = (kind: SessionEvent['kind'], at: number, actorType: SessionEvent['actorType'], actorId: string | null, detail: Record<string, unknown> = {}): SessionEvent => {
    serial += 1;
    return { id: `sev_${session.id.slice('ses_'.length, -2)}${CROCKFORD[Math.floor(serial / 32) % 32] ?? '0'}${CROCKFORD[serial % 32] ?? '0'}`, kind, at: iso(at), actorType, actorId, detail } as SessionEvent;
  };
  const player = session.playerId;
  const events: SessionEvent[] = [
    event('started', session.startedAt, session.origin === 'kiosk' ? 'player' : 'member', session.origin === 'kiosk' ? player : null, { funding: session.funding.kind === 'paid' ? 'postpaid' : session.funding.kind }),
  ];
  for (const added of session.added) {
    const minutes = Math.round(added.seconds / 60);
    if (added.kind === 'comp') events.push(event('comped', added.at, 'member', null, { minutes, reason: 'other' }));
    else if (added.kind === 'paid') events.push(event('sold_time', added.at, 'member', null, { minutes }));
    else events.push(event('pass_added', added.at, 'player', player, { minutes, name: added.label }));
  }
  if (session.lockedAt !== null && session.lockedAt <= now) events.push(event('locked', session.lockedAt, 'player', player, { reason: 'step_away' }));
  for (const minutes of [10, 5]) {
    const at = phase.endsAt - minutes * 60_000;
    if (at > session.startedAt && at <= now && (phase.endedAt === null || at < phase.endedAt)) events.push(event('warned', at, 'system', null, { minutes }));
  }
  if (phase.endsAt <= now && (phase.endedAt === null || phase.endReason === 'time_up')) events.push(event('grace_started', phase.endsAt, 'system', null));
  if (phase.endedAt !== null) {
    const reason = phase.endReason === 'logout' ? 'logout' : phase.endReason === 'staff' ? 'staff_ended' : 'grace_expired';
    events.push(event('ended', phase.endedAt, phase.endReason === 'logout' ? 'player' : phase.endReason === 'staff' ? 'member' : 'system', null, { endReason: reason }));
  }
  return events.sort((a, b) => a.at.localeCompare(b.at));
}

// -----------------------------------------------------------------------------------------------
// Calls and the kitchen
// -----------------------------------------------------------------------------------------------

function demoCallDto(call: DemoCall, state: DemoState, now: number): StationCall {
  const session = state.sessions.find((candidate) => candidate.stationId === call.stationId && candidate.startedAt <= call.openedAt && sessionPhase(candidate, call.openedAt).status !== 'ended');
  const closed = call.state === 'resolved' || call.state === 'cancelled';
  return {
    id: call.id,
    stationId: call.stationId,
    sessionId: session?.id ?? null,
    playerId: call.playerId,
    category: call.category,
    note: call.note,
    state: call.state,
    raisedAt: iso(call.openedAt),
    acknowledgedAt: call.state === 'open' ? null : iso(call.updatedAt <= now ? call.updatedAt : now),
    acknowledgedByName: call.staffName,
    closedAt: closed ? iso(call.updatedAt) : null,
  } as StationCall;
}

/** A kitchen ticket's id for an order: the order's own TypeID body under the ticket prefix. */
export const ticketIdOf = (orderId: string): string => `ktk_${orderId.slice('fod_'.length)}`;
const orderIdOf = (ticketId: string): string => `fod_${ticketId.slice('ktk_'.length)}`;
const CROCKFORD = '0123456789abcdefghjkmnpqrstvwxyz';
/** An item's id: the order's body with its marker character swapped for the line's index — unique per order and line. */
const itemIdOf = (orderId: string, index: number): string => {
  const body = orderId.slice('fod_'.length);
  return `kti_${body.slice(0, 15)}${CROCKFORD[index % 32] ?? '0'}${body.slice(16)}`;
};

const RANK: readonly DemoOrderStatus[] = ['new', 'accepted', 'preparing', 'ready', 'delivered'];

function demoTicketDto(order: DemoOrder): KitchenTicket {
  const reached = (wanted: DemoOrderStatus): string | null => (RANK.indexOf(order.status) >= RANK.indexOf(wanted) && order.status !== 'cancelled' ? iso(order.updatedAt) : null);
  return {
    id: ticketIdOf(order.id),
    orderId: order.id,
    saleId: null,
    orderNumber: order.number,
    paid: order.paidBy === 'wallet',
    venueId: DEMO_VENUE.id,
    area: 'kitchen',
    state: order.status,
    stationId: order.stationId,
    note: null,
    items: order.lines.map((line, index) => ({ id: itemIdOf(order.id, index), name: line.name, quantity: line.quantity, modifiers: [], note: null, sortOrder: index })),
    placedAt: iso(order.placedAt),
    acceptedAt: reached('accepted'),
    preparingAt: reached('preparing'),
    readyAt: reached('ready'),
    deliveredAt: order.status === 'delivered' ? iso(order.updatedAt) : null,
    updatedAt: iso(order.updatedAt),
  } as KitchenTicket;
}

/** Forward one step, back one step, or cancelled before delivery (spec/13 §6.2). */
function legalMove(from: DemoOrderStatus, to: DemoOrderStatus): boolean {
  if (to === 'cancelled') return from !== 'delivered' && from !== 'cancelled';
  const a = RANK.indexOf(from);
  const b = RANK.indexOf(to);
  return a >= 0 && b >= 0 && Math.abs(a - b) === 1;
}

// -----------------------------------------------------------------------------------------------
// The handler
// -----------------------------------------------------------------------------------------------

const paged = <Row>(rows: readonly Row[]): DemoAnswer => ({ status: 200, body: { data: rows, page: { cursor: null, hasMore: false, count: rows.length } } });
const ok = (body: unknown, status = 200): DemoAnswer => ({ status, body });

/** The staff name the demo console acts as on a call ("Riya is on the way"). */
const STAFF_NAME = 'Riya';

/**
 * Answer one request from the simulated venue, or `undefined` for a path it does not model. Reads use `store.read()`;
 * every write goes through `store.update()`, so the kiosk tab hears it.
 */
export function answerFromDemoVenue(
  store: DemoStore,
  method: string,
  pathname: string,
  query: URLSearchParams,
  body: Record<string, unknown>,
  now: number,
): DemoAnswer | undefined {
  if (!pathname.startsWith(`${ORGANIZATION_PATH}/`)) return undefined;
  if (!Object.values(DEMO_VENUE_PATHS).some((pattern) => pattern.test(pathname))) return undefined;
  const state = store.read();
  const notFound = (title: string) => problem(404, 'not_found', title);

  if (method === 'GET') {
    if (DEMO_VENUE_PATHS.floor.test(pathname)) return ok({ version: state.revision, stations: demoFloor(state, now) });
    if (DEMO_VENUE_PATHS.stations.test(pathname)) return paged(demoStations(state, now));
    if (DEMO_VENUE_PATHS.stationClasses.test(pathname)) return paged(demoStationClasses());
    if (DEMO_VENUE_PATHS.zones.test(pathname)) return paged(demoZones());
    if (DEMO_VENUE_PATHS.floorLive.test(pathname)) {
      const views = stationViews(state, now);
      const running = views.filter((view) => view.session !== null).length;
      const sessionsToday = state.sessions.filter((session) => session.startedAt >= state.epoch - 6 * 3_600_000).length;
      const takings = state.sessions.reduce((sum, session) => sum + chargedAmount(session, Math.max(0, Math.floor((now - session.startedAt) / 1000))), 0) +
        state.orders.filter((order) => order.status !== 'cancelled').reduce((sum, order) => sum + order.lines.reduce((total, line) => total + line.quantity * line.unitPrice, 0), 0);
      return ok({
        venueId: DEMO_VENUE.id,
        businessDay: iso(now).slice(0, 10),
        takings: money(takings),
        sessionsToday,
        sessionsNow: running,
        stationsInSession: running,
        stationsOffered: DEMO_STATIONS.length,
        occupancyNowBasisPoints: Math.round((running * 10_000) / DEMO_STATIONS.length),
        openCalls: state.calls.filter((call) => call.state === 'open' || call.state === 'acknowledged').length,
        computedAt: iso(now),
      });
    }
    const onStation = DEMO_VENUE_PATHS.stationSession.exec(pathname);
    if (onStation !== null) {
      const view = stationViews(state, now).find((candidate) => candidate.station.id === onStation[1]);
      return view?.session == null ? notFound('No such station, or it has no active session.') : ok(demoSessionDto(view.session, now));
    }
    const health = DEMO_VENUE_PATHS.stationHealth.exec(pathname);
    if (health !== null) {
      return ok({ stationId: health[1], status: 'healthy', open: [], recent: [], latest: null, policy: { currentVersion: 'pb1_demo', appliedVersion: 'pb1_demo', reportedAt: iso(now), upToDate: true } });
    }
    const telemetry = DEMO_VENUE_PATHS.stationTelemetry.exec(pathname);
    if (telemetry !== null) return ok({ stationId: telemetry[1], samples: [] });
    if (DEMO_VENUE_PATHS.needsAttention.test(pathname)) return ok({ alerts: [], commands: [] });
    const timeline = DEMO_VENUE_PATHS.sessionTimeline.exec(pathname);
    if (timeline !== null) {
      const session = sessionById(state, timeline[1] ?? '');
      return session === undefined ? notFound('No such session in this organisation.') : ok({ sessionId: session.id, segments: [], events: demoTimeline(session, now) });
    }
    const one = DEMO_VENUE_PATHS.session.exec(pathname);
    if (one !== null) {
      const session = sessionById(state, one[1] ?? '');
      return session === undefined ? notFound('No such session in this organisation.') : ok(demoSessionDto(session, now));
    }
    if (DEMO_VENUE_PATHS.calls.test(pathname)) {
      const calls = [...state.calls].sort((a, b) => b.openedAt - a.openedAt).map((call) => demoCallDto(call, state, now));
      return ok({ calls });
    }
    if (DEMO_VENUE_PATHS.kitchenTickets.test(pathname)) {
      const wanted = query.get('state');
      const area = query.get('area');
      if (area !== null && area !== 'kitchen') return ok({ tickets: [] });
      const tickets = state.orders
        .filter((order) => (wanted === null ? order.status !== 'delivered' && order.status !== 'cancelled' : order.status === wanted))
        .sort((a, b) => (wanted === 'delivered' ? b.updatedAt - a.updatedAt : a.placedAt - b.placedAt))
        .slice(0, wanted === 'delivered' ? 10 : undefined)
        .map(demoTicketDto);
      return ok({ tickets });
    }
    return problem(405, 'method_not_allowed', 'Method not allowed');
  }

  if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');

  // Staff start a session on a free PC (F-SES-04): comp minutes, or time sold at the counter.
  if (DEMO_VENUE_PATHS.startSession.test(pathname)) {
    const input = body as { stationId?: string; playerId?: string; funding?: { kind?: string; minutes?: number; budgetAmount?: { amount?: number } } };
    const station = stationById(input.stationId ?? '');
    if (station === undefined) return notFound('No such station at this venue.');
    const rate = classById(station.classId)?.hourlyRate ?? 6_000;
    const comp = input.funding?.kind === 'comp';
    const seconds = comp ? (input.funding?.minutes ?? 30) * 60 : Math.max(60, Math.floor(((input.funding?.budgetAmount?.amount ?? rate) / rate) * 3_600));
    let started: DemoSession | undefined;
    let refusal: string | undefined;
    store.update((current, at) => {
      const result = startSession(current, {
        stationId: station.id,
        playerId: input.playerId !== undefined && playerById(input.playerId) !== undefined ? input.playerId : null,
        now: at,
        origin: 'staff',
        staff: { seconds, kind: comp ? 'comp' : 'paid', label: comp ? 'Free time' : 'Sold at the counter' },
      });
      if (!result.ok) {
        refusal = result.refusal;
        return current;
      }
      started = result.value;
      return result.state;
    });
    if (started === undefined) {
      return refusal === 'already-in-session'
        ? problem(409, 'already_in_session', 'The player is already playing at another PC.')
        : problem(409, 'station_unavailable', 'This station already has a session.');
    }
    return ok(demoSessionDto(started, now), 201);
  }

  const callAction = DEMO_VENUE_PATHS.callAction.exec(pathname);
  if (callAction !== null) {
    const call = state.calls.find((candidate) => candidate.id === callAction[1]);
    if (call === undefined) return notFound('No such call at this venue.');
    if (call.state !== 'open' && call.state !== 'acknowledged') return problem(409, 'conflict', 'The call is already closed.');
    const next = store.update((current, at) => {
      const result = setCallState(current, {
        callId: call.id,
        state: callAction[2] === 'acknowledge' ? 'acknowledged' : 'resolved',
        staffName: callAction[2] === 'acknowledge' ? (call.staffName ?? STAFF_NAME) : call.staffName,
        now: at,
      });
      return result.ok ? result.state : current;
    });
    const updated = next.calls.find((candidate) => candidate.id === call.id);
    return updated === undefined ? notFound('No such call at this venue.') : ok(demoCallDto(updated, next, now));
  }

  const ticket = DEMO_VENUE_PATHS.kitchenTicketState.exec(pathname);
  if (ticket !== null) {
    const orderId = orderIdOf(ticket[1] ?? '');
    const order = state.orders.find((candidate) => candidate.id === orderId);
    if (order === undefined) return notFound('No such kitchen ticket at this venue.');
    const to = (body as { state?: DemoOrderStatus }).state;
    if (to === undefined || !legalMove(order.status, to)) return problem(409, 'conflict', 'That is not a legal transition from the ticket’s current state.');
    const next = store.update((current, at) => {
      const result = setOrderStatus(current, { orderId, status: to, now: at });
      return result.ok ? result.state : current;
    });
    const moved = next.orders.find((candidate) => candidate.id === orderId);
    return moved === undefined ? notFound('No such kitchen ticket at this venue.') : ok(demoTicketDto(moved));
  }

  const action = DEMO_VENUE_PATHS.sessionAction.exec(pathname);
  if (action === null) return problem(405, 'method_not_allowed', 'Method not allowed');
  const session = sessionById(state, action[1] ?? '');
  if (session === undefined) return notFound('No such session in this organisation.');
  if (sessionPhase(session, now).status === 'ended') return problem(409, 'session_already_ended', 'The session has already ended.');

  switch (action[2]) {
    case 'comp':
    case 'sell-time': {
      const minutes = Math.max(1, Math.min(24 * 60, Math.floor(Number((body as { minutes?: unknown }).minutes ?? 15))));
      const next = store.update((current, at) => {
        const result = addTime(current, {
          sessionId: session.id,
          seconds: minutes * 60,
          kind: action[2] === 'comp' ? 'comp' : 'paid',
          label: action[2] === 'comp' ? 'Free time' : 'Sold at the counter',
          now: at,
        });
        return result.ok ? result.state : current;
      });
      return ok(demoSessionDto(sessionById(next, session.id) ?? session, now));
    }
    case 'message': {
      const text = String((body as { text?: unknown }).text ?? '').trim();
      if (text === '') return problem(400, 'validation_failed', 'Validation failed', 'A message needs some text.');
      store.update((current, at) => {
        const result = sendMessage(current, { stationId: session.stationId, text, now: at });
        return result.ok ? result.state : current;
      });
      return ok({ id: `sev_${session.id.slice('ses_'.length, -2)}zz`, kind: 'message_sent', at: iso(now), actorType: 'member', actorId: null, detail: { text, needsAck: (body as { needsAck?: unknown }).needsAck === true } });
    }
    default: {
      const next = store.update((current, at) => {
        const result = endSession(current, { sessionId: session.id, reason: 'staff', now: at });
        return result.ok ? cancelCallAt(result.state, { stationId: session.stationId, now: at }) : current;
      });
      return ok(demoSessionDto(sessionById(next, session.id) ?? session, now));
    }
  }
}
